import { AttachmentCategory } from '@@types/api.types';
import { BadgeList } from '@components/shared/badge-list.component';
import { Tile } from '@components/shared/tile.component';
import { useGetTechnologyAttachments } from '@queries/attachments.query';
import { useGetTechnology } from '@queries/technology.query';
import { formatTimelineDate } from '@utils/assessment.util';
import { TechnologyHeaderTab } from '@utils/query-state.util';
import { pluralWord } from '@utils/text.util';
import { ArrowRightIcon, CalendarIcon, LinkIcon, TagIcon } from 'lucide-react';
import { FC, useMemo } from 'react';
import { useParams } from 'react-router-dom';

type ResearchCompactProps = {
  onNavigateToDetails?: (tab: TechnologyHeaderTab) => void;
};

export const ResearchCompact: FC<ResearchCompactProps> = (props) => {
  const { onNavigateToDetails } = props;
  const { technologyId } = useParams();
  const { data: technology, isPending: isTechnologyLoading } = useGetTechnology(technologyId);
  const { data: attachments = [], isLoading: isAttachmentsLoading } = useGetTechnologyAttachments({
    category: AttachmentCategory.RESEARCH_ARTEFACT,
    technologyId: technologyId ?? '',
  });

  const researchKeywords = technology?.metadata?.researchKeywords || [];
  const hasTimeline = Boolean(
    technology?.timeline?.startDate || (technology?.timeline?.startMonth && technology?.timeline?.startYear),
  );

  const referencesCount = attachments.length;
  const keywordsCount = researchKeywords.length;

  const keywordItems = useMemo(() => {
    return [...researchKeywords]
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
      .map((keyword) => ({ id: keyword, name: keyword }));
  }, [researchKeywords]);

  const isEmpty = referencesCount === 0 && keywordsCount === 0 && !hasTimeline;
  const canNavigate = Boolean(onNavigateToDetails);

  const handleNavigate = () => {
    onNavigateToDetails?.('details');
  };

  return (
    <Tile
      className="min-h-fit"
      emptyState={{ title: 'No Research Data' }}
      header={{
        iconButton: canNavigate
          ? {
              icon: <ArrowRightIcon size={16} />,
              onClick: handleNavigate,
            }
          : undefined,
        title: 'Research',
      }}
      hoverReveal={canNavigate}
      isEmpty={isEmpty && !isTechnologyLoading && !isAttachmentsLoading}
      isLoading={isTechnologyLoading || isAttachmentsLoading}
    >
      <div className="flex flex-col gap-3">
        {/* Timeline Summary */}
        {hasTimeline && technology?.timeline && (
          <div className="flex items-center gap-2 text-sm">
            <CalendarIcon
              className="text-gray-400 shrink-0"
              size={16}
            />
            <span className="text-gray-600">
              Started: <span className="font-medium text-gray-900">{formatTimelineDate(technology.timeline)}</span>
            </span>
          </div>
        )}

        {/* References Count */}
        {referencesCount > 0 && (
          <div className="flex items-center gap-2 text-sm">
            <LinkIcon
              className="text-gray-400 shrink-0"
              size={16}
            />
            <span className="text-gray-600">
              <span className="font-medium text-gray-900">{referencesCount}</span>{' '}
              {pluralWord(referencesCount, 'Reference')}
            </span>
          </div>
        )}

        {/* Keywords Preview */}
        {keywordsCount > 0 && (
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2 text-sm">
              <TagIcon
                className="text-gray-400 shrink-0"
                size={16}
              />
              <span className="text-gray-600">
                <span className="font-medium text-gray-900">{keywordsCount}</span>{' '}
                {pluralWord(keywordsCount, 'Keyword')}
              </span>
            </div>
            <div className="pl-6">
              <BadgeList
                badgeVariant="outline"
                items={keywordItems}
                limit={5}
                size="xs"
              />
            </div>
          </div>
        )}
      </div>
    </Tile>
  );
};
